class RoiCalc extends HTMLElement {
    constructor() {
        super()

        this.state = {
            sessions: 100000,
            cvr: 2,
            aov: 60,
            cvrUplift: 0,
            aovUplift: 0,
        }
        this.animateRevenueOnNextUpdate = false
        this.revenueValueTweens = new WeakMap()
    }

    connectedCallback() {
        this.cacheElements()
        this.bindEvents()
        this.initRangeFills()
        this.update()
    }

    cacheElements() {
        this.sessionsInput = this.querySelector('[data-sessions-input]')
        this.cvrInput = this.querySelector('[data-crv-input]')
        this.aovInput = this.querySelector('[data-aov-input]')

        this.sessionsRange = this.querySelector(
            'input[name="monthly-sessions"]'
        )

        this.cvrRange = this.querySelector(
            'input[name="CVR"]'
        )

        this.aovRange = this.querySelector(
            'input[name="AOV"]'
        )

        this.cvrButtons = [
            ...this.querySelectorAll('[data-crv-uplift]')
        ]

        this.aovButtons = [
            ...this.querySelectorAll('[data-aov-uplift]')
        ]

        this.roiBars = [
            ...this.querySelectorAll('[roi-bar-index]')
        ].sort((a, b) => {
            return Number(a.getAttribute('roi-bar-index')) - Number(b.getAttribute('roi-bar-index'))
        })
    }

    initRangeFills() {
        this.querySelectorAll('input[type="range"]').forEach((range) => {
            this.updateRangeFill(range)
            range.addEventListener('input', () => this.updateRangeFill(range))
        })
    }

    updateRangeFill(range) {
        const min = Number(range.min) || 0
        const max = Number(range.max) || 100
        const progress =
            ((Number(range.value) - min) / (max - min)) * 100

        range.style.setProperty('--range-progress', `${progress}%`)
    }

    bindEvents() {
        this.sessionsRange.addEventListener('input', () => {
            this.state.sessions = Number(this.sessionsRange.value)
            this.update()
        })

        this.cvrRange.addEventListener('input', () => {
            this.state.cvr =
                Math.round(Number(this.cvrRange.value) * 10) / 10
            this.update()
        })

        this.aovRange.addEventListener('input', () => {
            this.state.aov = Number(this.aovRange.value)
            this.update()
        })

        this.cvrButtons.forEach((button) => {
            button.addEventListener('click', () => {
                this.state.cvrUplift = Number(
                    button.dataset.crvUplift
                )
                this.animateRevenueOnNextUpdate = true

                this.updateButtonGroup(
                    this.cvrButtons,
                    button
                )

                this.update()
            })
        })

        this.aovButtons.forEach((button) => {
            button.addEventListener('click', () => {
                this.state.aovUplift = Number(
                    button.dataset.aovUplift
                )
                this.animateRevenueOnNextUpdate = true

                this.updateButtonGroup(
                    this.aovButtons,
                    button
                )

                this.update()
            })
        })
    }

    update() {
        const {
            sessions,
            cvr,
            aov,
            cvrUplift,
            aovUplift,
        } = this.state

        const projectedCVR =
            cvr * (1 + cvrUplift / 100)

        const projectedAOV =
            aov * (1 + aovUplift / 100)

        const currentOrders =
            sessions * (cvr / 100)

        const projectedOrders =
            sessions * (projectedCVR / 100)

        const currentMonthlyRevenue =
            currentOrders * aov

        const projectedMonthlyRevenue =
            projectedOrders * projectedAOV

        const monthlyRevenueDifference =
            projectedMonthlyRevenue -
            currentMonthlyRevenue

        const currentYearlyRevenue =
            currentMonthlyRevenue * 12

        const projectedYearlyRevenue =
            projectedMonthlyRevenue * 12

        const yearlyRevenueDifference =
            projectedYearlyRevenue -
            currentYearlyRevenue

        const currentRps = (cvr / 100) * aov
        const projectedRps = (projectedCVR / 100) * projectedAOV

        this.sessionsInput.value =
            this.number(sessions)

        this.cvrInput.value =
            this.formatPercent(cvr)

        this.aovInput.value =
            this.currency(aov)

        this.set(
            '[data-current-sessions]',
            this.number(sessions)
        )

        this.set(
            '[data-projected-sessions]',
            this.number(sessions)
        )

        this.set(
            '[data-sessions-difference]',
            '-'
        )

        this.set(
            '[data-current-cvr]',
            this.formatPercent(cvr)
        )

        this.set(
            '[data-projected-cvr]',
            this.formatPercent(projectedCVR)
        )

        this.set(
            '[data-cvr-difference]',
            this.formatPp(projectedCVR - cvr)
        )

        this.set(
            '[data-current-orders]',
            this.number(currentOrders)
        )

        this.set(
            '[data-projected-orders]',
            this.number(projectedOrders)
        )

        this.set(
            '[data-orders-difference]',
            `+${this.number(
                projectedOrders - currentOrders
            )}`
        )

        this.set(
            '[data-current-aov]',
            this.formatAov(aov)
        )

        this.set(
            '[data-projected-aov]',
            this.formatAov(projectedAOV)
        )

        this.set(
            '[data-aov-difference]',
            projectedAOV === aov
                ? '-'
                : `+${this.formatAov(
                    projectedAOV - aov
                )}`
        )

        this.set(
            '[data-current-rps]',
            this.formatRps(currentRps)
        )

        this.set(
            '[data-projected-rps]',
            this.formatRps(projectedRps)
        )

        this.set(
            '[data-rps-difference]',
            projectedRps === currentRps
                ? '-'
                : this.formatRpsDiff(projectedRps - currentRps)
        )

        this.set(
            '[data-current-monthly-revenue]',
            this.currency(currentMonthlyRevenue)
        )

        this.set(
            '[data-projected-monthly-revenue]',
            this.currency(projectedMonthlyRevenue)
        )

        this.set(
            '[data-monthly-revenue-difference]',
            `+${this.currency(
                monthlyRevenueDifference
            )}`
        )

        this.set(
            '[data-current-yearly-revenue]',
            this.currency(currentYearlyRevenue)
        )

        this.set(
            '[data-projected-yearly-revenue]',
            this.currency(projectedYearlyRevenue)
        )

        this.updateRevenueValue(
            '[data-yearly-revenue-summary]',
            yearlyRevenueDifference
        )

        this.set(
            '[data-yearly-revenue-difference]',
            `+${this.currency(
                yearlyRevenueDifference
            )}`
        )

        this.updateRevenueValue(
            '[data-monthly-revenue-increase]',
            monthlyRevenueDifference
        )

        this.updateRevenueValue(
            '[data-yearly-revenue-increase]',
            yearlyRevenueDifference,
            ' in yearly revenue'
        )

        this.updateRevenueHighlights()
        this.syncRanges()
        this.updateRoiBars()
        this.animateRevenueOnNextUpdate = false
    }

    syncRanges() {
        const { sessions, cvr, aov } = this.state

        this.sessionsRange.value = sessions
        this.cvrRange.value = cvr
        this.aovRange.value = aov

            ;[this.sessionsRange, this.cvrRange, this.aovRange].forEach(
                (range) => this.updateRangeFill(range)
            )
    }

    updateButtonGroup(buttons, activeButton) {
        buttons.forEach((button) => {
            button.classList.remove(
                'bg-green-900',
                'text-white',
                'opacity-100'
            )

            button.classList.add(
                'bg-transparent',
                'text-green-900',
                'opacity-50'
            )
        })

        activeButton.classList.remove(
            'bg-transparent',
            'text-green-900',
            'opacity-50'
        )

        activeButton.classList.add(
            'bg-green-900',
            'text-white',
            'opacity-100'
        )
    }

    set(selector, value) {
        const element =
            this.querySelector(selector)

        if (!element) return

        element.textContent = value
    }

    updateRevenueValue(selector, nextValue, suffix = '') {
        const element = this.querySelector(selector)
        if (!element) return

        const shouldAnimate = this.animateRevenueOnNextUpdate && !!window.gsap
        if (!shouldAnimate) {
            element.textContent = `+${this.currency(nextValue)}${suffix}`
            return
        }

        const runningTween = this.revenueValueTweens.get(element)
        if (runningTween) runningTween.kill()

        const startValue = this.parseCurrencyValue(element.textContent)
        const counter = { value: startValue }

        const tween = window.gsap.to(counter, {
            value: nextValue,
            duration: 0.5,
            ease: 'power1.out',
            onUpdate: () => {
                element.textContent = `+${this.currency(counter.value)}${suffix}`
            },
            onComplete: () => {
                element.textContent = `+${this.currency(nextValue)}${suffix}`
                this.revenueValueTweens.delete(element)
            },
        })

        this.revenueValueTweens.set(element, tween)
    }

    parseCurrencyValue(text) {
        const numeric = (text || '').replace(/[^\d.,-]/g, '').replace(/\./g, '').replace(',', '.')
        const parsed = Number(numeric)
        return Number.isFinite(parsed) ? parsed : 0
    }

    updateRoiBars() {
        if (!this.roiBars?.length) return

        const { cvrUplift, aovUplift } = this.state
        const cvrProgress = Math.max(0, Math.min(1, cvrUplift / 25))
        const aovProgress = Math.max(0, Math.min(1, aovUplift / 10))
        const upliftProgress = (cvrProgress + aovProgress) / 2
        const easedProgress = Math.pow(upliftProgress, 0.6)

        const maxHeights = [40, 52, 66, 82, 96]

        this.roiBars.forEach((bar, idx) => {
            const targetHeight = Math.min(
                100,
                maxHeights[idx] * easedProgress
            )

            if (window.gsap) {
                window.gsap.to(bar, {
                    height: `${targetHeight}%`,
                    duration: 0.8,
                    ease: 'expo.out',
                    overwrite: 'auto',
                })
                return
            }

            bar.style.height = `${targetHeight}%`
        })
    }

    updateRevenueHighlights() {
        const hasUplift = this.state.cvrUplift > 0 || this.state.aovUplift > 0
        const targets = this.querySelectorAll(
            '[data-monthly-revenue-increase], [data-yearly-revenue-increase], [data-yearly-revenue-summary]'
        )

        targets.forEach((el) => {
            el.classList.add('transition-colors', 'duration-300')
            el.classList.toggle('text-green-400', hasUplift)
        })
    }

    euNumber(value, options = {}) {
        return new Intl.NumberFormat('nl-NL', options).format(
            value
        )
    }

    number(value) {
        return this.euNumber(Math.round(value), {
            maximumFractionDigits: 0,
        })
    }

    currency(value) {
        return `€${this.euNumber(Math.round(value), {
            maximumFractionDigits: 0,
        })}`
    }

    formatAov(value) {
        return `€${this.euNumber(value, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`
    }

    formatRps(value) {
        return `€${this.euNumber(value, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`
    }

    formatRpsDiff(value) {
        const sign = value >= 0 ? '+' : '-'
        return `${sign}${this.formatRps(Math.abs(value))}`
    }

    formatPercent(value) {
        return `${this.euNumber(value, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        })}%`
    }

    formatPp(value) {
        const formatted = this.euNumber(Math.abs(value), {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        })
        return `${value >= 0 ? '+' : '-'}${formatted}pp`
    }
}

customElements.define(
    'roi-calc',
    RoiCalc
)